import { Avatar } from './avatar';

import { Bubble } from './bubble';
import matchUrl from 'match-url-wildcard';
import {
  Configurations,
  ChatTranslations,
  MetaEntry,
  CustomConfig,
  TokensDto,
  DefaultLanguage,
  RefreshTokensDto,
  RefreshTokenData,
} from './types';
import _ from 'lodash';
import {
  DEFAULT_LANGUAGE,
  FALLBACK_LANGUAGE,
  META_STORAGE_NAME,
} from './placeholder.constants';
import { throwError } from 'rxjs';
import { sanitizeStyles } from './utils';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const defaultCss = require('./placeholder.css');

function makeRequest(
  url: URL,
  group = '',
  method?: string,
  data?: any,
): Promise<string> {
  const request = new XMLHttpRequest();

  return new Promise(function (resolve, reject) {
    request.onreadystatechange = function (): void {
      if (request.readyState !== 4) {
        return;
      }
      if (request.status >= 200 && request.status < 300) {
        resolve(request.responseText);
      } else {
        reject({
          status: request.status,
          statusText: request.statusText,
        });
      }
    };

    request.onerror = function (): void {
      reject({
        status: 0,
        statusText: 'Network error or CORS blocked',
      });
    };

    request.open(method || 'GET', url.toString(), true);
    const token = localStorage.getItem(`c_at_${group}`);

    if (token) {
      request.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    if (data) {
      request.setRequestHeader(
        'Content-Type',
        'Application/json; charset=utf-8',
      );
    }
    data ? request.send(JSON.stringify(data)) : request.send();
  });
}

function setupBubble(
  config: Configurations,
  baseUrl: URL,
  embedUrl: URL,
): void {
  // We are using a shadow root so that there are no clashes between
  // the customer’s and our own CSS.
  const wrapper = document.createElement('div');
  wrapper.id = 'kauz-root';
  wrapper.attachShadow({ mode: 'open' });
  document.body.appendChild(wrapper);

  const defaultStyle = document.createElement('style');
  defaultStyle.type = 'text/css';
  defaultStyle.innerHTML = defaultCss;
  wrapper.shadowRoot.appendChild(defaultStyle);

  const styleUrl = new window.URL(config.userStyle.url, baseUrl);
  const link = document.createElement('link');
  link.setAttribute('rel', 'stylesheet');
  link.setAttribute('href', styleUrl.toString());

  const customStyleLoaded = new Promise((res, rej) => {
    link.onload = res;
    link.onerror = rej;
    wrapper.shadowRoot.appendChild(link);
  });

  customStyleLoaded
    .catch(() => {
      console.log('failed to load user-supplied CSS');
    })
    .finally(() => {
      const avatar = new Avatar(config);
      const bubble = new Bubble({ url: embedUrl.toString() });
      wrapper.shadowRoot.appendChild(avatar.element);
      wrapper.shadowRoot.appendChild(bubble.element);
    });

  if (config.placeholderStyles) {
    const avatarURL = new window.URL(config.botAvatar, baseUrl);
    const style = document.createElement('style');
    style.appendChild(
      document.createTextNode(`
        #placeholder {
          ${
            config.placeholderStyles['mat-primary-500']
              ? `--mat-primary-500: ${config.placeholderStyles['mat-primary-500']};`
              : ''
          }
          ${
            config.placeholderStyles['mat-primary-contrast-500']
              ? `--mat-primary-contrast-500: ${config.placeholderStyles['mat-primary-contrast-500']};`
              : ''
          }
          ${
            config.placeholderStyles['mat-secondary-500']
              ? `--mat-secondary-500: ${config.placeholderStyles['mat-secondary-500']};`
              : ''
          }
          ${
            config.placeholderStyles['mat-secondary-contrast-500']
              ? `--mat-secondary-contrast-500: ${config.placeholderStyles['mat-secondary-contrast-500']};`
              : ''
          }
          ${
            config.placeholderStyles['chatbot-background-color']
              ? `--chatbot-background-color: ${config.placeholderStyles['chatbot-background-color']};`
              : ''
          }
          ${
            config.botAvatar
              ? `.bot.avatar {
                    background-image: url("${avatarURL}");
                  }`
              : ''
          }
        }`),
    );
    wrapper.shadowRoot.appendChild(style);
  }

  if (config.customStylesExpanded) {
    const style = document.createElement('style');
    style.appendChild(
      document.createTextNode(sanitizeStyles(config.customStylesExpanded)),
    );
    wrapper.shadowRoot.appendChild(style);
  }
}

const checkAndGetLanguage = async (
  config: Configurations,
  defaultLanguagesUrl: URL,
): Promise<string> => {
  const defaultLanguageResponse = (await makeRequest(
    defaultLanguagesUrl,
    config.bot.group,
    'GET',
  )) as string;
  const metaString = localStorage.getItem(
    `${META_STORAGE_NAME}_${config.bot.group}`,
  );
  const meta = metaString ? (JSON.parse(metaString) as MetaEntry[]) : [];
  const lngFromMeta = _.chain(meta)
    .find((i) => i.key === 'chat_i18n_language')
    .get('value')
    .value();
  let browserLanguage = navigator.language;
  browserLanguage = browserLanguage
    ? browserLanguage.split('-')[0]
    : browserLanguage;
  const defaultLanguage = (
    JSON.parse(defaultLanguageResponse) as DefaultLanguage
  ).code;
  const parentDocumentLanguage = window.document.documentElement.lang;
  console.log('Parent page language:', parentDocumentLanguage);
  const parentDocumentLanguageSanitized = _.chain(parentDocumentLanguage)
    .split('-')
    .head()
    .upperCase()
    .value();

  return (
    parentDocumentLanguageSanitized ||
    lngFromMeta ||
    defaultLanguage ||
    browserLanguage ||
    DEFAULT_LANGUAGE
  );
};

const getTranslations = async (
  config: Configurations,
  languagesServerUrl: URL,
  refreshTokenUrl: URL,
  refreshTokenData: RefreshTokenData,
): Promise<void> => {
  const maxRetries = 3;
  let retryCount = 0;

  const attemptRequest = async (): Promise<void> => {
    try {
      const result: string = await makeRequest(
        languagesServerUrl,
        config.bot.group,
        'GET',
      );

      if (result) {
        const translations = JSON.parse(result) as ChatTranslations;
        config.placeholder.avatar.toast.message = _.get(
          translations,
          'kauzChatGreeting',
          'Hallo!',
        );
        const wrapper = document.getElementById('kauz-root');

        if (wrapper && wrapper.shadowRoot) {
          const greetingElement = wrapper.shadowRoot.getElementById('greeting');

          if (greetingElement) {
            greetingElement.innerHTML = config.placeholder.avatar.toast.message;
          }
        }
      } else {
        console.error('Language configuration problem.');
        throwError(() => new Error('Language configuration problem.'));
      }
    } catch (e) {
      if (
        languagesServerUrl.searchParams.get('languageCode').toUpperCase() ===
          FALLBACK_LANGUAGE ||
        (e.status === 401 && retryCount >= maxRetries)
      ) {
        throwError(e);
      }

      if (e.status === 401) {
        retryCount++;
        await refreshTokenRequest(
          refreshTokenUrl,
          config.bot.group,
          refreshTokenData,
        );
        return attemptRequest();
      }

      languagesServerUrl.searchParams.set('languageCode', FALLBACK_LANGUAGE);
      return attemptRequest();
    }
  };

  return attemptRequest();
};

const refreshTokenRequest = async (
  refreshTokenUrl,
  group,
  refreshTokenData: RefreshTokenData,
): Promise<void> => {
  const result: string = await makeRequest(
    refreshTokenUrl,
    '',
    'POST',
    refreshTokenData,
  );
  const { accessToken } = JSON.parse(result) as RefreshTokensDto;

  if (accessToken) {
    localStorage.setItem(`c_at_${group}`, accessToken);
  }
};

// The placeholder runs under the customer’s URL. In order to get the
// configuration file running at our own URL without any explicit configuration
// on the customer’s side, we rely on the source URL this script is loaded from.

function initChat(currentScript) {
  const src = new window.URL(currentScript, window.location.href);
  const baseUrl = new window.URL('..', src);
  const embedUrl = new window.URL('..', src);
  embedUrl.search = src.search;
  const configUrl = new window.URL('assets/config/config.json', baseUrl);
  let config = {} as Configurations;

  const requestConfig = makeRequest(configUrl);
  requestConfig
    .then((conf: string) => {
      config = JSON.parse(conf) as Configurations;

      const tokenUrl = new window.URL(
        `${config.bot.moonkin.url}/api/v4/auth/jwts/token`,
      );
      const refreshTokenUrl = new window.URL(
        `${config.bot.moonkin.url}/api/v4/auth/jwts/refresh`,
      );
      const configServerUrl = new window.URL(
        `${config.bot.moonkin.url}/api/v4/chat/configurations`,
      );
      configServerUrl.search = src.search;
      const languagesServerUrl = new window.URL(
        `${config.bot.moonkin.url}/api/v4/chat/translations/one`,
      );
      languagesServerUrl.search = src.search;
      const defaultLanguagesUrl = new window.URL(
        `${config.bot.moonkin.url}/api/v4/chat/standard-languages/one`,
      );
      defaultLanguagesUrl.search = src.search;

      const group = configServerUrl.searchParams.get('group');
      config.bot.group = group;

      if (config.multiClientSupport && group) {
        makeRequest(tokenUrl, group, 'POST', { group }).then(
          (authResponse: string) => {
            const { accessToken, refreshToken, sessionId } = JSON.parse(
              authResponse,
            ) as TokensDto;

            if (accessToken) {
              localStorage.setItem(`c_at_${group}`, accessToken);
            }

            if (refreshToken) {
              localStorage.setItem(`c_rt_${group}`, refreshToken);
            }

            if (sessionId) {
              localStorage.setItem(`c_sid_${group}`, sessionId);
            }
            // Get configuration for the group
            makeRequest(configServerUrl, group, 'GET')
              .then(async (response: string) => {
                if (response) {
                  const customConfig = JSON.parse(response) as CustomConfig;
                  config.placeholder.avatar.toast = {
                    message: '',
                    placeholderTimeout:
                      customConfig.kauzChatPlaceholderGreeting,
                  };
                  // custom styles
                  config.placeholderStyles = customConfig.placeholderStyles;
                  config.customStylesExpanded = customConfig.customStyles;
                  config.botAvatar = customConfig.botAvatar;
                  config.supportedHosts =
                    customConfig.kauzChatCorsOrigin.split(',');

                  // Get languages configuration
                  const requiredLang = await checkAndGetLanguage(
                    config,
                    defaultLanguagesUrl,
                  );
                  languagesServerUrl.searchParams.set(
                    'languageCode',
                    requiredLang.toUpperCase(),
                  );
                  await getTranslations(
                    config,
                    languagesServerUrl,
                    refreshTokenUrl,
                    { refreshToken, sessionId },
                  );

                  // Track language change on the parent page
                  observeLangAttribute(
                    config,
                    languagesServerUrl,
                    refreshTokenUrl,
                    { refreshToken, sessionId },
                  );

                  // Only setup Bubble if kauzChatCorsOrigin is allowing the page to setup bubble
                  const windowURLIsAllowedByCustomCustomerCors = matchUrl(
                    window.location.href,
                    config.supportedHosts,
                  );

                  if (windowURLIsAllowedByCustomCustomerCors === false) {
                    throw new Error(
                      'This page is not allowed to embed this placeholder script by Kauz AIStudio Whitelist settings!',
                    );
                  } else {
                    setupBubble(config, baseUrl, embedUrl);
                  }
                } else {
                  console.error('Configuration problem.');
                }
              })
              .catch((e) => {
                console.error('Configuration problem.');
                console.error(e.message || e);
              });
          },
        );
      } else if (config.multiClientSupport && !group) {
        console.error('Configuration problem. Group have to be provided.');
      } else {
        setupBubble(config, baseUrl, embedUrl);
      }
    })
    .catch(console.log);
}

const observeLangAttribute = async (
  config: Configurations,
  languagesServerUrl: URL,
  refreshTokenUrl: URL,
  refreshTokenData: RefreshTokenData,
): Promise<void> => {
  // set logic for observing page language
  const targetNode = document.documentElement;
  const observerConfig = { attributes: true, attributeFilter: ['lang'] };
  const callback = async (mutationsList: any) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'lang') {
        const requiredLang = _.chain(targetNode.getAttribute('lang'))
          .split('-')
          .head()
          .upperCase()
          .value();

        if (requiredLang) {
          languagesServerUrl.searchParams.set('languageCode', requiredLang);
          await getTranslations(
            config,
            languagesServerUrl,
            refreshTokenUrl,
            refreshTokenData,
          );

          console.log(`Avatar lang: ${requiredLang}`);
        }
      }
    }
  };
  const observer = new MutationObserver(callback);
  observer.observe(targetNode, observerConfig);
  console.log('Observation of the lang attribute change for avatar started.');
};

try {
  const currentScript = document.currentScript.getAttribute('src');
  initChat(currentScript);
} catch (e) {
  console.log(e.message);
}

export default {
  initChat,
};
